import { createForm } from '@seek/forms-ui';
import { Box, Stack } from 'braid-design-system';
import { useState } from 'react';
import { z } from 'zod';

import {
  badWordsValidator,
  disallowHtmlValidator,
  isEmailValidator,
  maxLengthValidator,
  requiredArrayValidator,
  requiredStringValidator,
} from '../../../../utils/validator';

import { Form } from './components/Form/Form';
import { ThanksView } from './components/ThanksView/ThanksView';

const FORM_NAME = 'integration-request-form';
const TrackingProps = {
  page: 'integration-request',
  siteSection: 'partners',
};

const requiredFieldMessage = 'Required field';

const largestCustomerSchema = z
  .object({
    seekId: disallowHtmlValidator,
    name: disallowHtmlValidator,
  })
  .optional();

const EnquiryFormValueSchema = z.object({
  firstName: requiredStringValidator
    .and(disallowHtmlValidator)
    .and(maxLengthValidator(100))
    .and(badWordsValidator),

  lastName: requiredStringValidator
    .and(disallowHtmlValidator)
    .and(maxLengthValidator(100))
    .and(badWordsValidator),

  jobTitle: requiredStringValidator
    .and(disallowHtmlValidator)
    .and(maxLengthValidator(100))
    .and(badWordsValidator),

  phone: requiredStringValidator
    .and(maxLengthValidator(20))
    .and(badWordsValidator),

  email: requiredStringValidator
    .and(disallowHtmlValidator)
    .and(maxLengthValidator(100))
    .and(badWordsValidator)
    .and(isEmailValidator),

  company: requiredStringValidator
    .and(disallowHtmlValidator)
    .and(maxLengthValidator(100)),

  companyUrl: requiredStringValidator
    .and(disallowHtmlValidator)
    .and(maxLengthValidator(100)),

  companyLocation: requiredStringValidator
    .and(disallowHtmlValidator)
    .and(maxLengthValidator(100)),

  platformType: requiredStringValidator,

  platformTypeOther: z.string().optional(),

  seekRsp: z.enum(['yes', 'no'], { message: requiredFieldMessage }),

  recruitmentSoftware: z.string().optional(),

  solutions: requiredArrayValidator,

  whySeek: requiredStringValidator
    .and(disallowHtmlValidator)
    .and(badWordsValidator)
    .and(maxLengthValidator(300)),

  customerSegment: z.string().optional().and(maxLengthValidator(150)),

  numCustomers: z.string().optional(),

  largestCustomers: z.array(largestCustomerSchema).optional(),

  jobAds: z.string().optional(),

  integrateToSeek: z.enum(['yes', 'no'], { message: requiredFieldMessage }),

  reasonForIntegration: z.string().optional().and(maxLengthValidator(300)),

  currentlyIntegrated: z.enum(['yes', 'no'], {
    message: requiredFieldMessage,
  }),

  reviewDocs: z.boolean().refine((val) => val === true, {
    message:
      'Please review the SEEK API Terms of Use and our technical documentation',
  }),
});

const FormValueSchema = EnquiryFormValueSchema.extend({}).catchall(z.unknown());

export const EnhancedFormValueSchema = FormValueSchema.superRefine(
  async (value, ctx) => {
    // recruitmentSoftware is mandatory when seekRsp is "no"
    if (value.seekRsp === 'no' && !value.recruitmentSoftware) {
      ctx.addIssue({
        path: ['recruitmentSoftware'],
        message: requiredFieldMessage,
        code: z.ZodIssueCode.custom,
      });
    }

    // platformTypeOther is mandatory when platformType is "Other"
    if (value.platformType === 'Other' && !value.platformTypeOther) {
      ctx.addIssue({
        path: ['platformTypeOther'],
        message: requiredFieldMessage,
        code: z.ZodIssueCode.custom,
      });
    }

    // Validate customerSegment, numCustomers, largestCustomers, and jobAds when seekRsp is "yes"
    if (value.seekRsp === 'yes') {
      if (!value.customerSegment) {
        ctx.addIssue({
          path: ['customerSegment'],
          message: requiredFieldMessage,
          code: z.ZodIssueCode.custom,
        });
      }
      if (value.customerSegment) {
        const validators = [
          {
            schema: maxLengthValidator(100),
            errorPath: 'customerSegment',
          },
          {
            schema: disallowHtmlValidator,
            errorPath: 'customerSegment',
          },
        ];

        for (const { schema, errorPath } of validators) {
          const validationResult = await schema.safeParseAsync(
            value.customerSegment,
          );

          if (!validationResult.success) {
            ctx.addIssue({
              path: [errorPath],
              message: validationResult.error.errors[0].message,
              code: z.ZodIssueCode.custom,
            });
          }
        }
      }

      if (!value.numCustomers) {
        ctx.addIssue({
          path: ['numCustomers'],
          message: requiredFieldMessage,
          code: z.ZodIssueCode.custom,
        });
      }

      if (!value.jobAds) {
        ctx.addIssue({
          path: ['jobAds'],
          message: requiredFieldMessage,
          code: z.ZodIssueCode.custom,
        });
      }
    }

    const wantToIntegrate = (): boolean => value.integrateToSeek === 'yes';
    if (!wantToIntegrate() && !value.reasonForIntegration) {
      ctx.addIssue({
        path: ['reasonForIntegration'],
        message: requiredFieldMessage,
        code: z.ZodIssueCode.custom,
      });
    }
  },
);

export type FormValues = z.infer<typeof EnhancedFormValueSchema>;

const { FormProvider } = createForm<FormValues>(FORM_NAME);

const initialValues: FormValues = {
  firstName: '',
  lastName: '',
  jobTitle: '',
  phone: '',
  email: '',
  company: '',
  companyUrl: '',
  companyLocation: '',
  platformType: '',
  platformTypeOther: '',
  seekRsp: 'yes',
  recruitmentSoftware: '',
  customerSegment: '',
  numCustomers: '',
  largestCustomers: [],
  jobAds: '0',
  integrateToSeek: 'yes',
  reasonForIntegration: '',
  currentlyIntegrated: 'yes',
  solutions: [],
  whySeek: '',
  reviewDocs: false,
};

export const EnquiryForm = () => {
  const [submitted, setSubmitted] = useState(false);

  return (
    <Box>
      <Box>
        {submitted ? (
          <ThanksView setSubmitted={setSubmitted} /> //eslint-disable-line
        ) : (
          <Stack space="xlarge">
            <FormProvider initialValues={initialValues}>
              {({ resetForm }) => (
                <Form
                  setSubmitted={setSubmitted}
                  resetForm={resetForm}
                  trackingProps={TrackingProps}
                  initialValues={initialValues}
                />
              )}
            </FormProvider>
          </Stack>
        )}
      </Box>
    </Box>
  );
};
